import type { User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';

import { auth } from './firebase';
import { loadAuthProfile } from './profile';
import { captureException } from './sentry';
import type { User } from '../types';

/**
 * Auth/profile state shared by the app shell and protected pages.
 */
export interface AuthProfileState {
  /** Current Firebase auth user. */
  firebaseUser: FirebaseUser | null;
  /** Current MeshOS public profile. */
  profile: User | null;
  /** Whether Firebase auth state is still resolving. */
  authLoading: boolean;
  /** Whether the MeshOS profile is still resolving. */
  profileLoading: boolean;
  /** Last user-visible auth/profile error. */
  error: string | null;
}

/**
 * Auth store transition events used by the Firebase listener.
 */
export type AuthTransition =
  | { type: 'auth-loading' }
  | { type: 'signed-out'; error?: string | null }
  | { type: 'signed-in'; firebaseUser: FirebaseUser }
  | { type: 'profile-loaded'; profile: User | null }
  | { type: 'profile-error'; error: string };

type Listener = () => void;

let state: AuthProfileState = {
  firebaseUser: null,
  profile: null,
  authLoading: true,
  profileLoading: false,
  error: null,
};
let unsubscribeAuth: (() => void) | null = null;
let loadToken = 0;
const listeners = new Set<Listener>();

const notify = (): void => {
  for (const listener of listeners) {
    listener();
  }
};

/**
 * Applies a pure auth/profile store transition.
 */
export function applyAuthTransition(
  current: AuthProfileState,
  transition: AuthTransition
): AuthProfileState {
  switch (transition.type) {
  case 'auth-loading':
    return {
      ...current,
      authLoading: true,
      error: null,
    };
  case 'signed-out':
    return {
      firebaseUser: null,
      profile: null,
      authLoading: false,
      profileLoading: false,
      error: transition.error ?? null,
    };
  case 'signed-in':
    return {
      firebaseUser: transition.firebaseUser,
      profile: null,
      authLoading: false,
      profileLoading: true,
      error: null,
    };
  case 'profile-loaded':
    return {
      ...current,
      profile: transition.profile,
      profileLoading: false,
      error: transition.profile ? null : 'User awaiting email verification',
    };
  case 'profile-error':
    return {
      ...current,
      profile: null,
      profileLoading: false,
      error: transition.error,
    };
  }
};

/**
 * Returns the current auth/profile state.
 */
export function getAuthState(): AuthProfileState {
  return state;
}

/**
 * Subscribes to auth/profile state changes.
 */
export function subscribeAuthProfile(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Starts the Firebase auth listener once for the app session.
 */
export function startAuthProfileStore(): void {
  if (unsubscribeAuth) {
    return;
  }

  state = applyAuthTransition(state, { type: 'auth-loading' });
  unsubscribeAuth = onAuthStateChanged(
    auth,
    (firebaseUser) => {
      const token = ++loadToken;

      if (!firebaseUser) {
        state = applyAuthTransition(state, { type: 'signed-out' });
        notify();
        return;
      }

      state = applyAuthTransition(state, { type: 'signed-in', firebaseUser });
      notify();

      void loadAuthProfile(firebaseUser)
        .then((profile) => {
          if (token !== loadToken) {
            return;
          }
          state = applyAuthTransition(state, { type: 'profile-loaded', profile });
          notify();
        })
        .catch((error: unknown) => {
          if (token !== loadToken) {
            return;
          }
          captureException(error);
          console.error('failed to fetch user profile:', error);
          state = applyAuthTransition(state, {
            type: 'profile-error',
            error: 'Failed to fetch user profile.',
          });
          notify();
        });
    },
    (error) => {
      captureException(error);
      state = applyAuthTransition(state, {
        type: 'signed-out',
        error: error.message,
      });
      notify();
    }
  );
}

/**
 * Refreshes the current MeshOS profile from Firebase.
 */
export async function refreshAuthProfile(): Promise<void> {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) {
    state = applyAuthTransition(state, { type: 'signed-out' });
    notify();
    return;
  }

  const token = ++loadToken;
  state = applyAuthTransition(state, { type: 'signed-in', firebaseUser });
  notify();

  const profile = await loadAuthProfile(firebaseUser);
  if (token === loadToken) {
    state = applyAuthTransition(state, { type: 'profile-loaded', profile });
    notify();
  }
}

/**
 * Resets the store during unit tests.
 */
export function resetAuthProfileStoreForTests(nextState?: Partial<AuthProfileState>): void {
  loadToken += 1;
  state = {
    firebaseUser: null,
    profile: null,
    authLoading: true,
    profileLoading: false,
    error: null,
    ...nextState,
  };
  notify();
}
